import type { MonitoringBot } from "@/lib/api/types";
import { formatDateTime } from "@/lib/format";
import { ROBOTS_CLASS, ROBOTS_LABEL, accessClass, accessDetail } from "@/lib/monitoring";

const LATER = "folgt mit S18 (Logfiles)";

/** Bot-Zugriffsmatrix: robots.txt-Status, UA-Test und die Logfile-Spalten (folgen mit S18). */
export function BotMatrix({ bots, checkedAt }: { bots: MonitoringBot[]; checkedAt: string | null }) {
  return (
    <section aria-labelledby="matrix-title" className="rounded-lg border border-line bg-white">
      <div className="flex flex-wrap justify-between gap-2 border-b border-[#e6e9e7] px-5 py-4">
        <h2 id="matrix-title" className="text-base font-semibold">
          Bot-Zugriffsmatrix
        </h2>
        <span className="text-[13px] text-muted">
          UA-Test simuliert nur den User-Agent; maßgeblich sind die verifizierten Logfile-Hits
          {checkedAt ? ` · UA-Test vom ${formatDateTime(checkedAt)}` : " · UA-Test noch nicht gelaufen"}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              <th className="px-5 py-2.5 font-medium">Bot</th>
              <th className="px-3 py-2.5 font-medium">Anbieter</th>
              <th className="px-3 py-2.5 font-medium">Zweck</th>
              <th className="px-3 py-2.5 font-medium">robots.txt</th>
              <th className="px-3 py-2.5 font-medium">UA-Test</th>
              <th className="px-3 py-2.5 text-right font-medium" title={LATER}>
                Hits 7 T
              </th>
              <th className="px-3 py-2.5 text-right font-medium" title={LATER}>
                verifiziert
              </th>
              <th className="px-5 py-2.5 font-medium" title={LATER}>
                Zuletzt
              </th>
            </tr>
          </thead>
          <tbody>
            {bots.map((bot) => (
              <tr key={bot.token} className="border-t border-neutral-bg align-top">
                <td className="px-5 py-2.5 font-mono text-[13px]">{bot.token}</td>
                <td className="px-3 py-2.5">{bot.vendor}</td>
                <td className="px-3 py-2.5 text-[#4a525b]">{bot.purpose}</td>
                <td className="px-3 py-2.5">
                  {bot.robots ? (
                    <div className="flex flex-col gap-0.5">
                      <span
                        className={`w-fit whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${ROBOTS_CLASS[bot.robots.status]}`}
                      >
                        {ROBOTS_LABEL[bot.robots.status]}
                      </span>
                      {bot.robots.disallow.length > 0 && bot.robots.status !== "blocked" && (
                        <span className="font-mono text-[11px] text-muted">
                          Disallow: {bot.robots.disallow.slice(0, 2).join(", ")}
                          {bot.robots.disallow.length > 2 ? " …" : ""}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {bot.access ? (
                    <div className="flex flex-col gap-0.5">
                      <span
                        title={bot.robots_only ? "reiner robots-Token, kein eigener Crawler" : undefined}
                        className={`w-fit whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${accessClass(bot.access.label)}`}
                      >
                        {bot.access.label}
                      </span>
                      {accessDetail(bot.access) && (
                        <span className="text-[11px] text-muted">{accessDetail(bot.access)}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">nicht getestet</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[13px] text-muted" title={LATER}>
                  —
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[13px] text-muted" title={LATER}>
                  —
                </td>
                <td className="px-5 py-2.5 text-muted" title={LATER}>
                  —
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="border-t border-neutral-bg px-5 py-3 text-xs text-muted">
        Hits, verifizierte Hits und „Zuletzt“ kommen mit dem Logfile-Import (S18). Ein Request mit gefälschtem
        User-Agent kommt nicht von der IP-Adresse des Anbieters, WAF-Regeln für verifizierte Bots können abweichen.
      </p>
    </section>
  );
}
